/**
 * @file LegalEntityCreateDialog.tsx
 * @description Создание юр.лица с DaData lookup (phase08.5 shadcn).
 */

import { Search } from "lucide-react";
import { useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import { legalEntities, type LegalEntityDto } from "../../lib/api-customers";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (le: LegalEntityDto) => void;
}

export function LegalEntityCreateDialog({ open, onClose, onCreated }: Props) {
  const notify = useNotify();
  const [inn, setInn] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [kpp, setKpp] = useState("");
  const [ogrn, setOgrn] = useState("");
  const [legalAddress, setLegalAddress] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [dadataSynced, setDadataSynced] = useState(false);

  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lookupInfo, setLookupInfo] = useState<string | null>(null);

  async function onLookup() {
    setError(null);
    setLookupInfo(null);
    if (!/^\d{10}$|^\d{12}$/.test(inn.trim())) {
      setError("ИНН должен быть 10 или 12 цифр.");
      return;
    }
    setLooking(true);
    try {
      const res = await legalEntities.lookupByInn(inn.trim());
      if (res.found && res.suggested) {
        setDisplayName(res.suggested.displayName);
        setKpp(res.suggested.kpp ?? "");
        setOgrn(res.suggested.ogrn ?? "");
        setLegalAddress(res.suggested.legalAddress);
        setDadataSynced(true);
        setLookupInfo(
          `Найдено в DaData${res.suggested.state ? ` (статус: ${res.suggested.state})` : ""}`,
        );
      } else {
        setLookupInfo("Не найдено в DaData. Заполните вручную.");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Ошибка DaData");
    } finally {
      setLooking(false);
    }
  }

  async function onSubmit() {
    setError(null);
    if (!inn.trim() || !displayName.trim() || !legalAddress.trim()) {
      setError("ИНН, название и юридический адрес обязательны.");
      return;
    }
    setSaving(true);
    try {
      const created = await legalEntities.create({
        displayName: displayName.trim(),
        inn: inn.trim(),
        kpp: kpp.trim() || null,
        ogrn: ogrn.trim() || null,
        legalAddress: legalAddress.trim(),
        bankDetails: null,
        contactPhone: contactPhone.trim() || null,
        contactEmail: contactEmail.trim() || null,
        notes: notes.trim() || null,
        dadataSynced,
      });
      notify.success("Юр.лицо создано");
      onCreated(created);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Не удалось сохранить";
      setError(msg);
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Новое юр.лицо</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 overflow-y-auto">
          {error ? <Alert severity="error">{error}</Alert> : null}
          {lookupInfo ? <Alert severity="info">{lookupInfo}</Alert> : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="le-inn">ИНН *</Label>
            <div className="flex gap-2">
              <Input
                id="le-inn"
                value={inn}
                onChange={(e) => setInn(e.target.value)}
                required
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => void onLookup()}
                disabled={looking}
              >
                <Search className="size-4" />
                {looking ? "Ищем…" : "DaData"}
              </Button>
            </div>
            <p className="text-xs text-on-surface-variant">
              10 цифр — ЮЛ, 12 цифр — ИП
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="le-name">Наименование *</Label>
            <Input
              id="le-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="le-addr">Юридический адрес *</Label>
            <Textarea
              id="le-addr"
              value={legalAddress}
              onChange={(e) => setLegalAddress(e.target.value)}
              required
              rows={2}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="le-kpp">КПП</Label>
              <Input id="le-kpp" value={kpp} onChange={(e) => setKpp(e.target.value)} />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="le-ogrn">ОГРН</Label>
              <Input id="le-ogrn" value={ogrn} onChange={(e) => setOgrn(e.target.value)} />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="le-phone">Контактный телефон</Label>
              <Input
                id="le-phone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="le-email">Контактный email</Label>
              <Input
                id="le-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="le-notes">Заметки</Label>
            <Textarea
              id="le-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} disabled={saving}>
            {saving ? "Создаём…" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
